// src/follower/components/FollowerMarketChart.jsx
// Fork of MarketChart.jsx — adapted for followerMarketEvent flat payload.
//
// KEY DIFFERENCES FROM MASTER MarketChart:
//   1. No extractStory() — reads flat eval_* fields directly from each bar
//   2. No gen_ai_msg / GenAI signal strip — master-only
//   3. No predLine series (up/down/pv from model predictions) — not in follower event
//   4. Tooltip built from flat fields, not from eval_agent_story blob
//   5. Guide panel rewritten for follower audience
//   6. selectedPosition shape comes from servantPositionEvent fields
//      (contract_symbol, entry_fill_price, exit_fill_price, option_type, realized_pnl)
//
// IDENTICAL TO MASTER:
//   - OHLC candlestick/line/area series
//   - PROCEED dot markers (confidence bucket → size + color)
//   - ADJUST ring markers (risk count → color)
//   - S/R horizontal annotation lines
//   - Entry/exit xaxis markers when position selected
//   - Chart type toggle (CANDLE/LINE/AREA)
//   - Window toggle (LAST 30 / LAST 60 / FULL DAY)
//   - xRange windowing + zoom/pan
//   - Position info strip
//
// Props:
//   data             — followerBars array (from followerMarketEvent + tradeEvent merge)
//   selectedPosition — follower open position object (optional), shape:
//     {
//       master_position_id,   // used as id for ADJUST bar matching
//       contract_symbol,
//       option_type,          // "call" | "put"
//       entry_fill_price,
//       exit_fill_price,      // null if open
//       closed_at,            // null if open
//       realized_pnl,
//       qty,
//     }
//   defaultChartType — "candlestick" | "line" | "area"  (default: "candlestick")
//   defaultWindow    — "last30" | "last60" | "full"      (default: "last30")

import React, { memo, useMemo, useState, useEffect, useRef } from "react";
import ReactApexChart from "react-apexcharts";

// ============================================================================
// DESIGN TOKENS — identical to master
// ============================================================================
const C = {
    bg:     "#08101e",
    bg1:    "#0c1525",
    bg2:    "#101b2e",
    border: "#1e2d42",
    text:   "#e8edf5",
    body:   "#b8c8dc",
    meta:   "#6a7d95",
    green:  "#00e896",
    cyan:   "#00c8ff",
    amber:  "#ffb340",
    red:    "#ff4466",
    blue:   "#4488ff",
    purple: "#9966ff",
    font:   "'DM Mono', monospace",
};

// ============================================================================
// HELPERS
// ============================================================================

const fmt = (v, dec = 2) => (v != null && !isNaN(v) ? Number(v).toFixed(dec) : "—");

// Flat-field confidence helpers — same logic as master, no story extraction needed
const confColor = (bucket) => ({
    VERY_HIGH: C.green, HIGH: C.green, MEDIUM: C.cyan, LOW: C.amber, VERY_LOW: C.red,
})[bucket] || C.meta;

const confMarkerSize = (bucket) => ({
    VERY_HIGH: 12, HIGH: 10, MEDIUM: 8, LOW: 6, VERY_LOW: 5,
})[bucket] || 7;

const dayTypeColor = (dayType) => ({
    TREND_DAY:        C.green,
    EVENT_TREND:      C.cyan,
    STRONG_TREND_DAY: C.green,
    WEAK_TREND_DAY:   "#88D400",
    RANGE_DAY:        C.blue,
    SIDEWAYS_DAY:     C.purple,
    CHOPPY_DAY:       C.red,
    VOLATILE_DAY:     C.amber,
    EVENT_DAY:        C.cyan,
})[dayType] || C.meta;

// ============================================================================
// FLAT EVAL EXTRACTOR
// Reads followerMarketEvent flat eval_* fields from a bar.
// Returns null if bar has no eval data.
// This replaces extractStory(bar) from the master chart entirely.
// ============================================================================
function extractFlatEval(bar) {
    if (!bar || !bar.eval_decision) return null;
    return {
        decision:         bar.eval_decision,
        confidenceBucket: bar.eval_confidence_bucket || null,
        confidence:       bar.eval_confidence ?? null,
        dayType:          bar.eval_day_type || null,
        entryType:        bar.eval_entry_type || null,
        eventDay:         bar.eval_event_day ?? false,
        volatilityCtx:    bar.eval_volatility_context || null,
        rrPreview:        bar.eval_rr_preview ?? null,
        rrFloor:          bar.eval_rr_floor ?? null,
        supportLevel:     bar.eval_support_level ?? null,
        resistanceLevel:  bar.eval_resistance_level ?? null,
        mindChanged:      bar.eval_mind_changed ?? false,
        initialDecision:  bar.eval_initial_decision || null,
        finalDecision:    bar.eval_final_decision || null,
        narrative:        bar.eval_narrative_summary || null,
        memCases:         bar.eval_memory_cases ?? 0,
        memorySimilarity: bar.eval_memory_similarity ?? 0,
        riskFlagCount:    bar.eval_risk_flag_count ?? 0,
        supportsDecision: bar.eval_supports_decision ?? true,
        expectedValue:    bar.eval_expected_value ?? null,
        patternName:      bar.eval_pattern_name || null,
        patternConf:      bar.eval_pattern_confidence ?? null,
        optionStrategy:   bar.eval_option_strategy || null,
        executionPath:    bar.eval_execution_path || null,
        timeframeLock:    bar.eval_timeframe_lock ?? false,
        timeframeLockMin: bar.eval_timeframe_lock_min ?? 0,
        positionId:       bar.eval_position_id || bar.position_id || null,
        newTarget:        bar.eval_new_target ?? null,
        newStop:          bar.eval_new_stop_loss ?? null,
    };
}

// Builds evalMap keyed by timestamp ms — same pattern as master
function buildEvalMap(data) {
    const map = {};
    if (!data) return map;
    data.forEach((bar) => {
        if (bar.eval_decision) {
            const ts = new Date(bar.t).getTime();
            map[ts] = {
                bar,
                eval: extractFlatEval(bar),
                close: parseFloat(bar.close || bar.c || 0),
            };
        }
    });
    return map;
}

// ============================================================================
// TOOLTIP HTML BUILDERS
// Flat-field version — no story blob needed
// ============================================================================
const tr = (label, value, color = C.body) =>
    `<div style="display:flex;justify-content:space-between;gap:12px;padding:2px 0">
    <span style="color:${C.meta};font-size:10px">${label}</span>
    <span style="color:${color};font-size:10px;font-weight:500">${value}</span>
  </div>`;

const divider = () =>
    `<div style="border-top:1px solid ${C.border};margin:5px 0"></div>`;

const tpill = (text, color) =>
    `<span style="display:inline-block;padding:1px 5px;font-size:9px;
   background:${color}18;border:1px solid ${color}50;border-radius:3px;
   color:${color};font-weight:500;margin-right:3px">${text}</span>`;

function buildEvalSection(ev) {
    if (!ev) return "";
    let html = divider();

    // Decision + confidence + day type pills
    const decColor = {
            PROCEED: C.green, HOLD: C.cyan, ADJUST: C.amber,
            CLOSE: C.red, SKIP: C.meta, SKIP_TRADE: C.meta,
        }[ev.decision] || C.meta;

    html += `<div style="margin-bottom:5px">
    ${tpill(ev.decision || "—", decColor)}
    ${ev.confidenceBucket ? tpill(ev.confidenceBucket, confColor(ev.confidenceBucket)) : ""}
    ${ev.dayType ? tpill(ev.dayType.replace(/_/g, " "), dayTypeColor(ev.dayType)) : ""}
    ${ev.entryType ? tpill(ev.entryType.replace(/_/g, " "), C.cyan) : ""}
    ${ev.executionPath === "fast_path" ? tpill("FAST PATH", C.purple) : ""}
  </div>`;

    // Mind change warning
    if (ev.mindChanged && ev.initialDecision && ev.finalDecision) {
        html += `<div style="color:${C.amber};font-size:9px;margin-bottom:5px">
      Mind change: ${ev.initialDecision} → ${ev.finalDecision}
    </div>`;
    }

    // Narrative summary — the plain-English reason
    if (ev.narrative) {
        html += `<div style="color:${C.body};font-size:10px;margin-bottom:5px;
      padding:4px 6px;background:${C.bg}18;border-left:2px solid ${decColor};border-radius:2px;
      line-height:1.5;max-width:260px">${ev.narrative}</div>`;
    }

    html += divider();

    // R:R
    if (ev.rrPreview != null && ev.rrFloor != null) {
        const rrOk = ev.rrPreview >= ev.rrFloor;
        html += tr("R:R",
            `${fmt(ev.rrPreview, 2)} · floor ${fmt(ev.rrFloor, 2)} — ${rrOk ? "above" : "BELOW FLOOR"}`,
            rrOk ? C.green : C.red);
    }

    // Volatility context
    if (ev.volatilityCtx) {
        html += tr("Volatility", ev.volatilityCtx, C.meta);
    }

    // Memory
    if (ev.memCases != null) {
        const memColor = ev.memCases === 0 ? C.red : ev.memCases < 5 ? C.amber : C.green;
        html += tr("Memory",
            ev.memCases === 0
                ? "No prior cases"
                : `${ev.memCases} cases (${fmt(ev.memorySimilarity * 100, 0)}% match)`,
            memColor);
    }

    // Pattern
    if (ev.patternName) {
        html += tr("Pattern",
            `${ev.patternName}${ev.patternConf != null ? ` ${fmt(ev.patternConf * 100, 0)}%` : ""}`,
            C.purple);
    }

    // Risk flags
    if (ev.riskFlagCount > 0) {
        html += tr("Risk flags", `${ev.riskFlagCount} active`,
            ev.riskFlagCount >= 4 ? C.red : ev.riskFlagCount >= 2 ? C.amber : C.meta);
    }

    // S/R levels
    if (ev.supportLevel)    html += tr("Support",    `$${fmt(ev.supportLevel)}`,    C.green + "cc");
    if (ev.resistanceLevel) html += tr("Resistance", `$${fmt(ev.resistanceLevel)}`, C.red   + "cc");

    // Event day note
    if (ev.eventDay) {
        html += `<div style="color:${C.cyan};font-size:9px;margin-top:4px">⚡ Event day active</div>`;
    }

    // Timeframe lock
    if (ev.timeframeLock) {
        html += `<div style="color:${C.amber};font-size:9px;margin-top:4px">
      🔒 Timeframe lock — ${ev.timeframeLockMin}m remaining
    </div>`;
    }

    return html;
}

function buildCandleTooltip(o, h, l, c, timeStr, evalData) {
    const isUp = (c || 0) >= (o || 0);
    return `<div style="background:${C.bg1};border:1px solid ${C.border};padding:10px 14px;
    font-family:${C.font};min-width:200px;max-width:280px">
    <div style="color:${C.meta};font-size:10px;margin-bottom:6px">${timeStr}</div>
    ${tr("O", `$${fmt(o)}`)}
    ${tr("H", `$${fmt(h)}`, C.green)}
    ${tr("L", `$${fmt(l)}`, C.red)}
    ${tr("C", `$${fmt(c)}`, isUp ? C.green : C.red)}
    ${evalData ? buildEvalSection(evalData.eval) : ""}
  </div>`;
}

function buildLineTooltip(closeVal, timeStr, evalData) {
    return `<div style="background:${C.bg1};border:1px solid ${C.border};padding:10px 14px;
    font-family:${C.font};min-width:200px;max-width:280px">
    <div style="color:${C.meta};font-size:10px;margin-bottom:6px">${timeStr}</div>
    ${tr("Price", `$${fmt(closeVal)}`, C.text)}
    ${evalData ? buildEvalSection(evalData.eval) : ""}
  </div>`;
}

// ============================================================================
// SHARED UI ATOMS
// ============================================================================
const Pill = ({ children, color = C.meta }) => (
    <span style={{
    display: "inline-flex", alignItems: "center", padding: "2px 7px",
    fontSize: "9px", fontFamily: C.font, fontWeight: 500,
    letterSpacing: "0.04em", textTransform: "uppercase",
    color, background: color + "18", border: `1px solid ${color}50`,
    borderRadius: "4px", lineHeight: "16px",
  }}>{children}</span>
);

const ToggleBtn = ({ label, active, onClick }) => (
    <button onClick={onClick} style={{
    padding: "3px 10px", fontSize: "10px", fontFamily: C.font,
    fontWeight: active ? 500 : 400,
    color:      active ? C.text : C.meta,
    background: active ? C.bg2  : "transparent",
    border:     `1px solid ${active ? C.border : "transparent"}`,
    borderRadius: "4px", cursor: "pointer",
    transition: "all 0.15s", lineHeight: "18px",
  }}>{label}</button>
);

// ============================================================================
// GUIDE PANEL — follower-focused copy
// ============================================================================
const LineSymbol = ({ color, dashed, thick }) => (
    <svg width={32} height={12} style={{ flexShrink: 0, display: "block" }}>
        <line x1="0" y1="6" x2="32" y2="6"
              stroke={color} strokeWidth={thick ? 2.5 : 1}
              strokeDasharray={dashed ? "5,3" : "none"} />
    </svg>
);

const CircleSymbol = ({ fill, stroke, size = 8 }) => (
    <svg width={size + 4} height={size + 4} style={{ flexShrink: 0, display: "block" }}>
        <circle cx={(size + 4) / 2} cy={(size + 4) / 2} r={size / 2}
                fill={fill} stroke={stroke} strokeWidth="1.5" />
    </svg>
);

const GuideSection = ({ title, color, children }) => (
    <div style={{ marginBottom: "24px" }}>
        <div style={{
      fontSize: "8px", color, letterSpacing: "0.12em", textTransform: "uppercase",
      marginBottom: "12px", paddingBottom: "5px", borderBottom: `1px solid ${color}30`,
    }}>{title}</div>
        <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>{children}</div>
    </div>
);

const GuideRow = ({ symbol, heading, body }) => (
    <div style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
        <div style={{ paddingTop: "1px", flexShrink: 0 }}>{symbol}</div>
        <div>
            <div style={{ fontSize: "11px", color: C.body, fontWeight: 500, marginBottom: "3px" }}>{heading}</div>
            <div style={{ fontSize: "10px", color: C.meta, lineHeight: "1.55" }}>{body}</div>
        </div>
    </div>
);

const ConfidenceScale = () => (
    <div style={{ display: "flex", alignItems: "flex-end", gap: "10px", margin: "8px 0 4px" }}>
        {[
            { size: 5,  fill: C.red,   label: "VERY LOW" },
            { size: 6,  fill: C.amber, label: "LOW" },
            { size: 8,  fill: C.cyan,  label: "MEDIUM" },
            { size: 10, fill: C.green, label: "HIGH" },
            { size: 12, fill: C.green, label: "VERY HIGH" },
            ].map(({ size, fill, label }) => (
        <div key={label} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "5px" }}>
            <svg width={14} height={14}>
                <circle cx={7} cy={7} r={size / 2} fill={fill} stroke={fill} strokeWidth="1" />
            </svg>
        <span style={{ fontSize: "7px", color: C.meta, textAlign: "center",
          fontFamily: C.font, letterSpacing: "0.03em", lineHeight: 1.2 }}>{label}</span>
        </div>
            ))}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "5px", marginLeft: "6px" }}>
            <svg width={14} height={14}>
                <circle cx={7} cy={7} r={5} fill={C.bg1} stroke={C.cyan} strokeWidth="1.5" />
            </svg>
      <span style={{ fontSize: "7px", color: C.meta, textAlign: "center",
        fontFamily: C.font, letterSpacing: "0.03em", lineHeight: 1.2 }}>HOLLOW<br/>= no mem</span>
        </div>
    </div>
);

const FollowerChartGuidePanel = ({ open, onClose }) => {
    const panelRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    useEffect(() => {
        if (!open) return;
        const onDown = (e) => {
            if (panelRef.current && !panelRef.current.contains(e.target)) onClose();
        };
        const t = setTimeout(() => document.addEventListener("mousedown", onDown), 50);
        return () => { clearTimeout(t); document.removeEventListener("mousedown", onDown); };
    }, [open, onClose]);

    return (
        <div
            ref={panelRef}
            style={{
        position: "fixed", top: 0, left: 0, bottom: 0, zIndex: 1001,
        width: "340px",
        background: C.bg1,
        borderRight: `1px solid ${C.border}`,
        transform: open ? "translateX(0)" : "translateX(-100%)",
        transition: "transform 0.22s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
        display: "flex", flexDirection: "column",
        fontFamily: C.font,
        boxShadow: open ? "6px 0 40px rgba(0,0,0,0.55)" : "none",
        pointerEvents: open ? "auto" : "none",
      }}
        >
            {/* Header */}
            <div style={{
        padding: "18px 20px 14px", borderBottom: `1px solid ${C.border}`,
        display: "flex", alignItems: "flex-start", justifyContent: "space-between",
        flexShrink: 0,
      }}>
                <div>
                    <div style={{ fontSize: "8px", color: C.meta, letterSpacing: "0.12em",
            textTransform: "uppercase", marginBottom: "5px" }}>HOW TO READ THIS CHART</div>
                    <div style={{ fontSize: "12px", color: C.text, lineHeight: "1.55", maxWidth: "260px" }}>
                        The BerBul AI evaluates SPY every minute. This chart shows what it decided, how confident it was, and why — directly from the master system.
                    </div>
                </div>
                <button
                    onClick={onClose}
                    style={{
            background: "transparent", border: `1px solid ${C.border}`,
            borderRadius: "4px", color: C.meta, fontFamily: C.font,
            fontSize: "11px", padding: "3px 8px", cursor: "pointer",
            flexShrink: 0, marginLeft: "10px", marginTop: "2px",
          }}
                    onMouseEnter={e => { e.target.style.borderColor = C.text; e.target.style.color = C.text; }}
                    onMouseLeave={e => { e.target.style.borderColor = C.border; e.target.style.color = C.meta; }}
                >✕</button>
            </div>

            {/* Scrollable content */}
            <div style={{ flex: 1, overflowY: "auto", padding: "20px",
        scrollbarWidth: "thin", scrollbarColor: `${C.border} transparent` }}>

                {/* SECTION 1 — Entry markers */}
                <GuideSection title="ENTRY MARKERS" color={C.cyan}>
                    <div style={{ fontSize: "10px", color: C.meta, lineHeight: "1.55", marginBottom: "4px" }}>
                        A circle appears on each bar where the master AI decided to trade. Size = confidence. Fill = whether past similar setups were found in memory.
                    </div>
                    <ConfidenceScale />
                    <GuideRow
                        symbol={<CircleSymbol fill={C.green} stroke={C.green} size={12} />}
                        heading="Large filled — high confidence + memory found"
                        body="AI entered with strong conviction and found similar historical trades. Highest quality signal." />
                    <GuideRow
                        symbol={<CircleSymbol fill={C.bg1} stroke={C.cyan} size={8} />}
                        heading="Hollow ring — no memory cases"
                        body="Zero similar past trades found. AI is acting on model signals alone. Treat with more caution regardless of confidence label." />
                    <GuideRow
                        symbol={<CircleSymbol fill={C.amber} stroke={C.amber} size={6} />}
                        heading="Small amber — low confidence"
                        body="AI entered but conviction was limited. Often seen on range days or choppy conditions." />
                </GuideSection>

                {/* SECTION 2 — Adjustment markers */}
                <GuideSection title="ADJUSTMENT MARKERS" color={C.amber}>
                    <div style={{ fontSize: "10px", color: C.meta, lineHeight: "1.55", marginBottom: "4px" }}>
                        ADJ markers appear when the master AI updates the target or stop on an open position. Your position levels are updated at the same time.
                    </div>
                    <GuideRow
                        symbol={
              <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center",
                width: 24, height: 24, borderRadius: "50%", background: C.cyan + "25",
                border: `1.5px solid ${C.cyan}`, fontSize: "7px", fontFamily: C.font,
                fontWeight: 600, color: C.cyan, flexShrink: 0 }}>ADJ</span>
            }
                        heading="ADJ cyan — clean adjustment"
                        body="Target or stop moved with low risk. Bot had clear conviction in the repositioning." />
                    <GuideRow
                        symbol={
              <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center",
                width: 24, height: 24, borderRadius: "50%", background: C.amber + "25",
                border: `1.5px solid ${C.amber}`, fontSize: "7px", fontFamily: C.font,
                fontWeight: 600, color: C.amber, flexShrink: 0 }}>ADJ</span>
            }
                        heading="ADJ amber — moderate risk"
                        body="Multiple risk flags active at adjustment time. Bot moved levels despite elevated uncertainty." />
                </GuideSection>

                {/* SECTION 3 — Price levels */}
                <GuideSection title="PRICE LEVELS" color={C.meta}>
                    <GuideRow symbol={<LineSymbol color={C.amber} dashed={false} thick />}
                              heading="Entry — your fill price"
                              body="The price at which your position was opened." />
                    <GuideRow symbol={<LineSymbol color={C.green} dashed thick />}
                              heading="Target — original TP"
                              body="Master bot's original take-profit level at entry." />
                    <GuideRow symbol={<LineSymbol color={C.cyan} dashed={false} thick />}
                              heading="Adj Target — updated TP"
                              body="Take-profit level after an ADJ event. Replaces the original." />
                    <GuideRow symbol={<LineSymbol color={C.red} dashed thick />}
                              heading="Stop — current stop loss"
                              body="Your stop-loss level. Position closes automatically if price reaches this." />
                    <GuideRow symbol={<LineSymbol color={C.green} dashed={true} thick={false} />}
                              heading="S — support zone"
                              body="Price level identified by the AI's pattern analysis as structural support." />
                    <GuideRow symbol={<LineSymbol color={C.red} dashed={true} thick={false} />}
                              heading="R — resistance zone"
                              body="Price level the AI flagged as likely resistance. Positions may see pressure here." />
                </GuideSection>

                {/* SECTION 4 — Tooltip guide */}
                <GuideSection title="HOVER TOOLTIP" color={C.purple}>
                    <GuideRow symbol={<span style={{ fontSize: "12px" }}>💬</span>}
                              heading="Hover any bar for AI reasoning"
                              body="Every bar with a circle shows the AI's decision, confidence bucket, narrative summary, R:R ratio, memory case count, and any pattern detected. Bars without circles are price-only." />
                    <GuideRow symbol={<span style={{ fontSize: "12px" }}>⚡</span>}
                              heading="EVENT DAY flag"
                              body="A cyan vertical line marks when the AI detected a macro event affecting SPY. The bot applies extra caution filters on event days." />
                    <GuideRow symbol={<span style={{ fontSize: "12px" }}>🔒</span>}
                              heading="Timeframe lock"
                              body="Appears in tooltip when the bot has locked the position open for a minimum hold period. No early exit is possible until the lock expires." />
                </GuideSection>

                {/* Footer */}
                <div style={{
          paddingTop: "14px", borderTop: `1px solid ${C.border}`,
          fontSize: "9px", color: C.meta, lineHeight: "1.6",
        }}>
                    <div style={{ marginBottom: "6px", color: C.body, fontSize: "10px" }}>Controls</div>
                    <div style={{ marginBottom: "4px" }}>
                        <span style={{ color: C.body }}>CANDLE / LINE / AREA</span> — changes price display. AI markers stay in all modes.
                    </div>
                    <div style={{ marginBottom: "4px" }}>
                        <span style={{ color: C.body }}>LAST 30 / LAST 60</span> — windows the visible range. Full session data stays loaded.
                    </div>
                    <div>
                        <span style={{ color: C.body }}>FULL DAY</span> — shows entire session from open. Best for reviewing the complete trade arc.
                    </div>
                </div>
            </div>
        </div>
    );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const FollowerMarketChart = ({
    data,
    selectedPosition,
    defaultChartType = "candlestick",
    defaultWindow    = "last30",
    }) => {

    const [chartType,  setChartType]  = useState(defaultChartType);
    const [viewWindow, setViewWindow] = useState(defaultWindow);
    const [xRange,     setXRange]     = useState({ min: undefined, max: undefined });
    const [showGuide,  setShowGuide]  = useState(false);

    // ── xRange window ─────────────────────────────────────────────────────────
    useEffect(() => {
        if (!data || data.length === 0) return;
        if (viewWindow === "full") { setXRange({ min: undefined, max: undefined }); return; }
        const barCount = viewWindow === "last30" ? 30 : 60;
        const lastTs   = new Date(data[data.length - 1].t).getTime();
        const firstTs  = lastTs - (barCount - 1) * 60_000;
        setXRange({ min: firstTs, max: lastTs + 2 * 60_000 });
    }, [data, viewWindow]);

    // ── Eval map (keyed by timestamp ms) ──────────────────────────────────────
    const evalMap = useMemo(() => buildEvalMap(data), [data]);

    // ── Latest data ────────────────────────────────────────────────────────────
    const latestData = useMemo(() =>
        (!data || data.length === 0) ? {} : data[data.length - 1] || {}, [data]);

    // ── Latest eval for header pill ───────────────────────────────────────────
    const latestEval = useMemo(() => {
        if (!data || data.length === 0) return null;
        for (let i = data.length - 1; i >= 0; i--) {
            const ev = extractFlatEval(data[i]);
            if (ev?.dayType) return ev;
        }
        return null;
    }, [data]);

    // ── PROCEED bars ──────────────────────────────────────────────────────────
    const proceedBars = useMemo(() => {
        if (!data || data.length === 0) return [];
        return data
            .filter(bar => bar.eval_decision === "PROCEED" || bar.eval_decision === "BUY")
            .map(bar => ({
                timestamp: new Date(bar.t).getTime(),
                price:     parseFloat(bar.close || bar.c || 0),
                eval:      extractFlatEval(bar),
            }));
    }, [data]);

    // ── ADJUST extraction ─────────────────────────────────────────────────────
    // Groups by eval_position_id; tracks cumulative latest target/stop
    const allAdjustmentsData = useMemo(() => {
        if (!data || data.length === 0) return [];
        const byPos = {};
        data.forEach((bar, index) => {
            const posId = bar.eval_position_id || bar.position_id;
            if (posId && bar.eval_decision === "ADJUST") {
                if (!byPos[posId]) byPos[posId] = [];
                byPos[posId].push({
                    positionId: posId,
                    timestamp:  new Date(bar.t).getTime(),
                    newTarget:  parseFloat(bar.eval_new_target || 0),
                    newStop:    parseFloat(bar.eval_new_stop_loss || 0),
                    price:      parseFloat(bar.close || bar.c || 0),
                    barIndex:   index,
                    eval:       extractFlatEval(bar),
                });
            }
        });
        const all = [];
        Object.values(byPos).forEach(arr => all.push(...arr));
        return all;
    }, [data]);

    // ── Selected position's adjustments ──────────────────────────────────────
    // selectedPosition.master_position_id used as the position key
    const selectedPositionData = useMemo(() => {
        if (!selectedPosition || !data || data.length === 0)
            return { adjustments: [], currentTarget: null, currentStop: null,
                originalTarget: 0, originalStop: 0 };

        const posId = selectedPosition.master_position_id ||
            selectedPosition.id ||
            selectedPosition.a_id;

        const adjustments = [];
        let latestTarget = selectedPosition.underlying_target ||
            selectedPosition.target || 0;
        let latestStop   = selectedPosition.underlying_stop  ||
            selectedPosition.stop  || 0;
        const originalTarget = latestTarget;
        const originalStop   = latestStop;

        data.forEach((bar) => {
            if ((bar.eval_position_id || bar.position_id) === posId &&
                bar.eval_decision === "ADJUST") {
                const adj = {
                    timestamp: new Date(bar.t).getTime(),
                    newTarget: bar.eval_new_target || latestTarget,
                    newStop:   bar.eval_new_stop_loss || latestStop,
                    price:     parseFloat(bar.close || 0),
                    eval:      extractFlatEval(bar),
                };
                adjustments.push(adj);
                if (adj.newTarget) latestTarget = adj.newTarget;
                if (adj.newStop)   latestStop   = adj.newStop;
            }
        });

        return { adjustments, currentTarget: latestTarget, currentStop: latestStop,
            originalTarget, originalStop };
    }, [selectedPosition, data]);

    // ── OHLC series ───────────────────────────────────────────────────────────
    const ohlc = useMemo(() => {
        if (!data || data.length === 0) return [];
        return data.map(({ t, open, high, low, close }) => ({
            x: new Date(t).getTime(),
            y: [parseFloat(open)||0, parseFloat(high)||0,
                parseFloat(low)||0,  parseFloat(close)||0],
        }));
    }, [data]);

    const closeLine = useMemo(() => {
        if (!data || data.length === 0) return [];
        return data.map(({ t, close }) =>
            ({ x: new Date(t).getTime(), y: parseFloat(close)||null }));
    }, [data]);

    // ── Derived header values ──────────────────────────────────────────────────
    const isClosed    = selectedPosition &&
        (selectedPosition.exit_fill_price || selectedPosition.closed_at);
    const latestClose = latestData.close ? parseFloat(latestData.close) : null;
    const prevClose   = data && data.length > 1
        ? parseFloat(data[data.length - 2]?.close || latestClose) : latestClose;
    const priceUp     = latestClose != null && latestClose >= (prevClose ?? latestClose);

    const { adjustments, currentTarget, currentStop, originalTarget, originalStop }
        = selectedPositionData;

    // ── Annotations ───────────────────────────────────────────────────────────
    const positionAnnotations = useMemo(() => {
        const ann = { yaxis: [], xaxis: [], points: [] };

        // S/R lines — flat fields, deduplicated across all eval bars
        const srSeen = new Set();
        if (data) {
            data.forEach((bar) => {
                const ev = extractFlatEval(bar);
                if (!ev) return;
                [
                    { type: "support",    val: ev.supportLevel },
                    { type: "resistance", val: ev.resistanceLevel },
                ].forEach(({ type, val }) => {
                    if (!val) return;
                    const key = `${type}_${Math.round(val * 10)}`;
                    if (srSeen.has(key)) return;
                    srSeen.add(key);
                    ann.yaxis.push({
                        y:               val,
                        borderColor:     type === "support" ? C.green + "70" : C.red + "70",
                        borderWidth:     1,
                        strokeDashArray: 6,
                        label: {
                            borderColor: "transparent",
                            style: { color: type === "support" ? C.green : C.red,
                                background: "transparent", fontSize: "9px", fontFamily: C.font },
                            text:     type === "support" ? `S ${fmt(val)}` : `R ${fmt(val)}`,
                            position: "right", offsetX: -6,
                        },
                    });
                });
            });
        }

        // ── No position selected — PROCEED markers + event day ────────────────
        if (!selectedPosition) {

            // Event day vertical line — first bar where eval_event_day is true
            const eventBar = data ? data.find(b => b.eval_event_day === true) : null;
            if (eventBar) {
                ann.xaxis.push({
                    x: new Date(eventBar.t).getTime(),
                    borderColor:     C.cyan + "60",
                    borderWidth:     1,
                    strokeDashArray: 4,
                    label: {
                        borderColor: C.cyan + "40",
                        style: {
                            color: C.cyan, background: C.bg, fontSize: "9px", fontFamily: C.font,
                            padding: { left: 4, right: 4, top: 2, bottom: 2 },
                        },
                        text: "EVENT DAY", orientation: "horizontal", offsetY: -4,
                    },
                });
            }

            // PROCEED dots
            proceedBars.forEach((pb) => {
                const ev     = pb.eval;
                const bucket = ev?.confidenceBucket || "MEDIUM";
                const color  = confColor(bucket);
                const size   = confMarkerSize(bucket);
                const memOk  = (ev?.memCases || 0) > 0;
                const label  = ev?.dayType
                    ? ev.dayType.replace(/_/g, " ") : null;
                const labelColor = ev?.dayType
                    ? dayTypeColor(ev.dayType) : C.meta;

                ann.points.push({
                    x: pb.timestamp, y: pb.price,
                    marker: {
                        size, shape: "circle",
                        fillColor:   memOk ? color : C.bg1,
                        strokeColor: color, strokeWidth: 2,
                    },
                    label: label ? {
                        text: label, offsetY: -(size + 6),
                        style: { fontSize: "8px", color: labelColor, background: C.bg,
                            fontFamily: C.font, padding: { left: 3, right: 3, top: 1, bottom: 1 } },
                    } : undefined,
                });
            });

            return ann;
        }

        // ── Position selected ─────────────────────────────────────────────────
        // Entry fill price line (amber solid)
        const entryPrice = selectedPosition.entry_fill_price ||
            selectedPosition.entryPrice || 0;
        if (entryPrice) {
            ann.yaxis.push({
                y: entryPrice, borderColor: C.amber, borderWidth: 2, strokeDashArray: 0,
                label: { borderColor: C.amber,
                    style: { color: C.bg, background: C.amber, fontSize: "10px", fontFamily: C.font },
                    text: `Entry $${Number(entryPrice).toFixed(2)}` },
            });
        }

        // Original target (green dashed)
        if (originalTarget > 0) {
            ann.yaxis.push({
                y: originalTarget, borderColor: C.green, borderWidth: 2, strokeDashArray: 4,
                label: { borderColor: C.green,
                    style: { color: C.bg, background: C.green, fontSize: "10px", fontFamily: C.font },
                    text: `Target $${Number(originalTarget).toFixed(2)}` },
            });
        }

        // Adjusted target (cyan solid) — only if different from original
        if (adjustments.length > 0 && currentTarget && currentTarget !== originalTarget) {
            ann.yaxis.push({
                y: currentTarget, borderColor: C.cyan, borderWidth: 2, strokeDashArray: 0,
                label: { borderColor: C.cyan,
                    style: { color: C.bg, background: C.cyan, fontSize: "10px", fontFamily: C.font },
                    text: `Adj Target $${Number(currentTarget).toFixed(2)}` },
            });
        }

        // Original stop (red dashed)
        if (originalStop > 0) {
            ann.yaxis.push({
                y: originalStop, borderColor: C.red, borderWidth: 2, strokeDashArray: 4,
                label: { borderColor: C.red,
                    style: { color: C.text, background: C.red, fontSize: "10px", fontFamily: C.font },
                    text: `Stop $${Number(originalStop).toFixed(2)}` },
            });
        }

        // Adjusted stop (red solid bold)
        if (adjustments.length > 0 && currentStop && currentStop !== originalStop) {
            ann.yaxis.push({
                y: currentStop, borderColor: C.red, borderWidth: 3, strokeDashArray: 0,
                label: { borderColor: C.red,
                    style: { color: C.text, background: C.red, fontSize: "10px",
                        fontFamily: C.font, fontWeight: "bold" },
                    text: `Adj Stop $${Number(currentStop).toFixed(2)}` },
            });
        }

        // Entry xaxis line + dot
        const entryTime = selectedPosition.open_at || selectedPosition.entryTime;
        if (entryTime) {
            const entryTs   = new Date(entryTime).getTime();
            const entryEval = evalMap[entryTs];
            const bucket    = entryEval?.eval?.confidenceBucket || "MEDIUM";
            const color     = confColor(bucket);
            const size      = confMarkerSize(bucket);
            const memOk     = (entryEval?.eval?.memCases || 0) > 0;
            const dayType   = entryEval?.eval?.dayType;
            const labelText  = dayType ? dayType.replace(/_/g, " ") : null;
            const labelColor = dayType ? dayTypeColor(dayType) : C.meta;

            ann.xaxis.push({
                x: entryTs, borderColor: C.amber, borderWidth: 2, strokeDashArray: 4,
                label: { borderColor: C.amber,
                    style: { color: C.bg, background: C.amber, fontSize: "10px",
                        fontFamily: C.font, fontWeight: "bold" },
                    text: "Entry", orientation: "horizontal" },
            });

            if (entryPrice) {
                ann.points.push({
                    x: entryTs, y: entryPrice,
                    marker: { size, shape: "circle",
                        fillColor:   memOk ? color : C.bg1,
                        strokeColor: color, strokeWidth: 2 },
                    label: labelText ? {
                        text: labelText, offsetY: -(size + 6),
                        style: { fontSize: "8px", color: labelColor, background: C.bg,
                            fontFamily: C.font, padding: { left: 3, right: 3, top: 1, bottom: 1 } },
                    } : undefined,
                });
            }
        }

        // Exit xaxis line + dot (for closed positions)
        const exitTime  = selectedPosition.closed_at || selectedPosition.exitTime;
        const exitPrice = selectedPosition.exit_fill_price || selectedPosition.exitPrice;
        const pnl       = selectedPosition.realized_pnl ?? selectedPosition.unrealizedPL ?? 0;
        if (exitTime) {
            const exitTs    = new Date(exitTime).getTime();
            const exitColor = pnl >= 0 ? C.green : C.red;
            ann.xaxis.push({
                x: exitTs, borderColor: exitColor, borderWidth: 2, strokeDashArray: 4,
                label: { borderColor: exitColor,
                    style: { color: pnl >= 0 ? C.bg : C.text, background: exitColor,
                        fontSize: "10px", fontFamily: C.font, fontWeight: "bold" },
                    text: "Exit", orientation: "horizontal" },
            });
            if (exitPrice) {
                ann.points.push({
                    x: exitTs, y: Number(exitPrice),
                    marker: { size: 8, fillColor: exitColor,
                        strokeColor: C.text, strokeWidth: 2, shape: "circle" },
                });
            }
        }

        // ADJUST markers for this position
        allAdjustmentsData
            .filter(adj => adj.positionId === (
            selectedPosition.master_position_id ||
            selectedPosition.id || selectedPosition.a_id))
            .forEach((adj) => {
                const rfCount = adj.eval?.riskFlagCount || 0;
                const mColor  = rfCount >= 4 ? C.red : rfCount >= 2 ? C.amber : C.cyan;
                const size    = rfCount >= 4 ? 11 : rfCount >= 2 ? 9 : 7;
                const changed = adj.eval?.mindChanged || false;

                ann.xaxis.push({
                    x: adj.timestamp, borderColor: mColor, strokeDashArray: 2, borderWidth: 1,
                    label: { borderColor: mColor,
                        style: { color: C.bg, background: mColor, fontSize: "9px", fontFamily: C.font },
                        text: changed ? "ADJ*" : "ADJ", orientation: "horizontal", offsetY: 0 },
                });

                ann.points.push({
                    x: adj.timestamp, y: adj.price,
                    marker: { size, shape: "circle",
                        fillColor:   changed ? C.bg1 : mColor,
                        strokeColor: mColor, strokeWidth: changed ? 3 : 2 },
                    label: adj.newStop ? {
                        text: `S ${fmt(adj.newStop)}`, offsetY: size + 8,
                        style: { fontSize: "9px", color: mColor, background: C.bg,
                            fontFamily: C.font, padding: { left: 3, right: 3, top: 1, bottom: 1 } },
                    } : undefined,
                });
            });

        // Cumulative adjustment target dots
        adjustments.forEach((adj, i) => {
            if (!adj.newTarget) return;
            ann.points.push({
                x: adj.timestamp, y: adj.newTarget,
                marker: { size: 10, fillColor: C.cyan, strokeColor: C.text,
                    strokeWidth: 2, shape: "circle" },
                label: { text: `Adj ${i + 1}`, offsetY: -18,
                    style: { fontSize: "9px", color: C.bg, background: C.cyan,
                        fontFamily: C.font, padding: { left: 3, right: 3, top: 1, bottom: 1 } } },
            });
        });

        return ann;
    }, [selectedPosition, selectedPositionData, allAdjustmentsData, proceedBars, evalMap, data]);

    // ── Chart options ─────────────────────────────────────────────────────────
    const chartOptions = useMemo(() => {
        const isCandle = chartType === "candlestick";

        return {
            chart: {
                type: isCandle ? "candlestick" : "line",
                height: 400, background: C.bg1, foreColor: C.body, fontFamily: C.font,
                zoom: { enabled: true, type: "x" },
                animations: { enabled: false },
                toolbar: { show: true,
                    tools: { download: true, selection: true, zoom: true,
                        zoomin: true, zoomout: true, pan: true, reset: true } },
            },
            theme: { mode: "dark" },
            grid: {
                show: true, borderColor: C.border, strokeDashArray: 3,
                xaxis: { lines: { show: true } }, yaxis: { lines: { show: false } },
            },
            plotOptions: {
                candlestick: { colors: { upward: C.green, downward: C.red },
                    wick: { useFillColor: true } },
            },
            stroke: {
                width: isCandle ? [1] : [2],
                curve: "smooth",
                colors: isCandle ? [C.meta] : [C.cyan],
            },
            fill: isCandle
                ? { type: ["solid"] }
                : { type: [chartType === "area" ? "gradient" : "solid"],
                gradient: { shade: "dark", type: "vertical", shadeIntensity: 0.3,
                    gradientToColors: [C.bg1],
                    opacityFrom: chartType === "area" ? 0.3 : 0, opacityTo: 0, stops: [0, 100] } },
            markers: { size: isCandle ? [0] : [0], strokeWidth: 1,
                strokeColors: C.bg, hover: { size: 5 } },
            xaxis: {
                type: "datetime", min: xRange.min, max: xRange.max,
                labels: {
                    formatter: (val) => new Date(val).toLocaleTimeString("en-US",
                        { hour: "numeric", minute: "numeric", hour12: false }),
                    style: { colors: C.meta, fontSize: "10px", fontFamily: C.font },
                },
                axisBorder: { show: true, color: C.border },
                axisTicks:  { show: true, color: C.border },
            },
            yaxis: {
                tooltip: { enabled: true },
                labels: {
                    formatter: (v) => v == null ? "" : "$" + v.toFixed(2),
                    style: { colors: C.meta, fontSize: "10px", fontFamily: C.font },
                },
                axisBorder: { show: true, color: C.border },
            },
            tooltip: {
                enabled: true,
                theme: "dark",
                shared: false,
                intersect: false,
                followCursor: true,
                style: { fontSize: "11px", fontFamily: C.font },
                custom: function ({ seriesIndex, dataPointIndex, w }) {
                    const ts      = w.globals.seriesX[seriesIndex]?.[dataPointIndex];
                    const timeStr = ts ? new Date(ts).toLocaleTimeString("en-US",
                        { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "";
                    const evalData = ts ? evalMap[ts] : null;

                    if (isCandle && seriesIndex === 0) {
                        const o = w.globals.seriesCandleO[seriesIndex]?.[dataPointIndex];
                        const h = w.globals.seriesCandleH[seriesIndex]?.[dataPointIndex];
                        const l = w.globals.seriesCandleL[seriesIndex]?.[dataPointIndex];
                        const c = w.globals.seriesCandleC[seriesIndex]?.[dataPointIndex];
                        return buildCandleTooltip(o, h, l, c, timeStr, evalData);
                    }

                    if (!isCandle && seriesIndex === 0) {
                        const closeVal = w.globals.series[seriesIndex]?.[dataPointIndex];
                        return buildLineTooltip(closeVal, timeStr, evalData);
                    }

                    return `<div style="padding:0"></div>`;
                },
            },
            legend: { show: false },
            annotations: positionAnnotations,
        };
    }, [chartType, xRange, positionAnnotations, evalMap]);

    // ── Series — price only (no model prediction lines) ───────────────────────
    const series = useMemo(() => {
        const isCandle  = chartType === "candlestick";
        const priceType = chartType === "area" ? "area" : isCandle ? "candlestick" : "line";
        return [
            { name: "Price", type: priceType, data: isCandle ? ohlc : closeLine },
        ];
    }, [chartType, ohlc, closeLine]);

    // ── Loading state ──────────────────────────────────────────────────────────
    if (!data || !Array.isArray(data) || data.length === 0) {
        return (
            <div style={{
        background: C.bg1, border: `1px solid ${C.border}`, borderRadius: "8px",
        height: "460px", display: "flex", alignItems: "center", justifyContent: "center",
        flexDirection: "column", gap: "10px",
        fontFamily: C.font, color: C.meta, fontSize: "12px",
      }}>
                <div style={{
          width: 8, height: 8, borderRadius: "50%", background: C.cyan,
          boxShadow: `0 0 8px ${C.cyan}`,
          animation: "fmc-pulse 1.6s ease-in-out infinite",
        }} />
                <style>{`@keyframes fmc-pulse {
          0%,100% { opacity:1; box-shadow:0 0 8px ${C.cyan}; }
          50%      { opacity:.4; box-shadow:0 0 3px ${C.cyan}; }
        }`}</style>
                Waiting for market data…
            </div>
        );
    }

    // ── Follower position display values ──────────────────────────────────────
    const posSymbol   = selectedPosition?.contract_symbol || latestData.symbol || "—";
    const posIsCall   = selectedPosition?.option_type === "call";
    const posEntryFmt = selectedPosition?.entry_fill_price
        ? `$${Number(selectedPosition.entry_fill_price).toFixed(2)}` : null;
    const posExitFmt  = selectedPosition?.exit_fill_price
        ? `$${Number(selectedPosition.exit_fill_price).toFixed(2)}` : null;
    const posPnl      = selectedPosition?.realized_pnl ?? null;
    const pnlColor    = posPnl == null ? C.meta : posPnl >= 0 ? C.green : C.red;
    const pnlFmt      = posPnl == null ? "--"
        : `${posPnl >= 0 ? "+" : ""}$${Math.abs(posPnl).toFixed(2)}`;

    // ── Render ─────────────────────────────────────────────────────────────────
    return (
        <div style={{ background: C.bg1, border: `1px solid ${C.border}`,
      borderRadius: "8px", padding: "12px 14px", fontFamily: C.font }}>

            {/* ── Position info strip ─────────────────────────────────────────── */}
            {selectedPosition && (
            <div style={{
          background: C.bg, border: `1px solid ${C.border}`, borderRadius: "6px",
          padding: "8px 12px", marginBottom: "10px",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: "8px", flexWrap: "wrap",
        }}>
                <div style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
            <span style={{ fontSize: "13px", fontWeight: 600, color: C.text }}>
              {posSymbol}
            </span>
                    <Pill color={posIsCall ? C.green : C.red}>
                        {posIsCall ? "CALL" : "PUT"}
                    </Pill>
                    {isClosed && (
                    <Pill color={posPnl >= 0 ? C.green : C.red}>
                        {posPnl >= 0 ? "WIN" : "LOSS"}
                    </Pill>
                        )}
                    {adjustments.length > 0 && (
                    <Pill color={C.amber}>{adjustments.length} ADJ</Pill>
                        )}
                </div>
                <div style={{ fontSize: "10px", color: C.meta }}>
                    {posEntryFmt && `Entry ${posEntryFmt}`}
                    {posExitFmt  && ` · Exit ${posExitFmt}`}
                    {posPnl != null && (
                    <span style={{ color: pnlColor, marginLeft: "6px" }}>· P&L {pnlFmt}</span>
                        )}
                    {adjustments.length > 0 && currentTarget && currentStop && (
                    <span style={{ color: C.amber, marginLeft: "8px" }}>
                T ${Number(originalTarget).toFixed(2)} → ${Number(currentTarget).toFixed(2)}&nbsp;
                        S ${Number(originalStop).toFixed(2)} → ${Number(currentStop).toFixed(2)}
              </span>
                        )}
                </div>
            </div>
                )}

            {/* ── Subtitle + guide button ─────────────────────────────────────── */}
            <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: "12px", marginBottom: "10px",
      }}>
        <span style={{ fontSize: "11px", color: C.body, lineHeight: "1.5" }}>
          AI-driven markers · hover any bar for reasoning ·{" "}
            <span style={{ color: C.cyan }}>S/R lines from agent pattern analysis</span>
        </span>
                <button
                    onClick={() => setShowGuide(true)}
                    style={{
            flexShrink: 0, background: "transparent",
            border: `1px solid ${C.cyan}60`, borderRadius: "4px",
            color: C.cyan, fontFamily: C.font, fontSize: "9px",
            padding: "3px 10px", cursor: "pointer",
            letterSpacing: "0.05em", whiteSpace: "nowrap",
            transition: "border-color 0.15s, background 0.15s",
          }}
                    onMouseEnter={e => { e.currentTarget.style.background = C.cyan + "15"; e.currentTarget.style.borderColor = C.cyan; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = C.cyan + "60"; }}
                >
                    HOW TO READ THIS CHART
                </button>
            </div>

            {/* ── Chart header ────────────────────────────────────────────────── */}
            <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        marginBottom: "8px", flexWrap: "wrap", gap: "6px",
      }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span style={{ fontSize: "10px", color: C.meta,
            letterSpacing: "0.06em", textTransform: "uppercase" }}>Market</span>
                    {(latestData.symbol || selectedPosition?.contract_symbol) && (
                        <span style={{ fontSize: "13px", fontWeight: 600, color: C.text }}>
                        {latestData.symbol || "SPY"}
                        </span>
                        )}
                    {latestClose != null && (
                    <span style={{ fontSize: "14px", fontWeight: 600,
              color: priceUp ? C.green : C.red }}>
              ${latestClose.toFixed(2)}
              <span style={{ fontSize: "10px", marginLeft: "3px", opacity: 0.8 }}>
                {priceUp ? "▲" : "▼"}
              </span>
            </span>
                        )}
                    {latestEval?.dayType && (
                        <Pill color={dayTypeColor(latestEval.dayType)}>
                        {latestEval.dayType.replace(/_/g, " ")}
                        </Pill>
                        )}
                    <Pill color={C.meta}>{data.length} bars</Pill>
                    {allAdjustmentsData.length > 0 && !selectedPosition && (
                    <Pill color={C.amber}>{allAdjustmentsData.length} adj</Pill>
                        )}
                </div>

                {/* Chart type toggle */}
                <div style={{ display: "flex", gap: "2px", background: C.bg,
          border: `1px solid ${C.border}`, borderRadius: "5px", padding: "2px" }}>
                    {["candlestick", "line", "area"].map((t) => (
                    <ToggleBtn key={t}
                               label={t === "candlestick" ? "CANDLE" : t.toUpperCase()}
                               active={chartType === t}
                               onClick={() => setChartType(t)} />
                        ))}
                </div>
            </div>

            {/* ── Main chart ──────────────────────────────────────────────────── */}
            <div style={{ background: C.bg1 }}>
                <ReactApexChart
                    key={chartType}
                    options={chartOptions}
                    series={series}
                    type={chartType === "candlestick" ? "candlestick" : "line"}
                    height={400}
                />
            </div>

            {/* ── Bottom controls ─────────────────────────────────────────────── */}
            <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        marginTop: "8px", flexWrap: "wrap", gap: "6px",
      }}>
                <div style={{ display: "flex", gap: "2px", background: C.bg,
          border: `1px solid ${C.border}`, borderRadius: "5px", padding: "2px" }}>
                    {[
                        { key: "last30", label: "LAST 30" },
                        { key: "last60", label: "LAST 60" },
                        { key: "full",   label: "FULL DAY" },
                        ].map(({ key, label }) => (
                    <ToggleBtn key={key} label={label}
                               active={viewWindow === key}
                               onClick={() => setViewWindow(key)} />
                        ))}
                </div>

        <span style={{ fontSize: "10px", color: C.meta }}>
          {selectedPosition ? "Position View" : "Session View"} · {data.length} bars
        </span>
            </div>

            {/* ── Guide panel ─────────────────────────────────────────────────── */}
            <FollowerChartGuidePanel open={showGuide} onClose={() => setShowGuide(false)} />
        </div>
    );
};

export default memo(FollowerMarketChart);